import React, { useCallback, useRef } from "react";

import {
  StackedCardContentMin,
  StackedCardResizeAxis,
  StackedCardResizePolicy,
  StackedCardResizeSource,
  StackedCardResizeStart,
  StackedCardSize,
  applyResize,
  isResizeKey,
  keyboardResize,
  makeStart,
  measurePercent,
} from "./resizePolicy";

/**
 * The one place pointer and keyboard input meet the resize policy.
 *
 * `StackedCard`'s built-in grip reports raw deltas and knows no policy — enough
 * for a host that just tracks a width. This hook is the full contract: it
 * captures the gesture start, funnels both inputs through the pure policy in
 * `resizePolicy.ts`, streams preview events and fires exactly ONE commit per
 * gesture. It returns the props to spread on the grip, including the live
 * separator ARIA.
 *
 * Preview versus commit (spec §7e): the continuous stream is live feedback
 * only. A host creates an undo entry on the COMMIT, never on a preview — and a
 * gesture that never changed the size commits nothing at all, so a bare click
 * on the grip is not a change.
 *
 * The hook never touches the card's content: the intrinsic minimum is read
 * through `contentMin`, which may be a getter so a card that measures its own
 * footer can answer at the moment it is asked.
 */

export interface UseStackedCardResizeArgs {
  /** The card's current size, owned by the host. */
  size: StackedCardSize;
  policy: StackedCardResizePolicy;
  /** Id of the element the grip resizes — becomes `aria-controls`. */
  cardId: string;
  /** The card's intrinsic minimum; a getter is read at the moment it is needed. */
  contentMin: StackedCardContentMin | (() => StackedCardContentMin);
  /** `width` locks the vertical axis (auto-height cards). Defaults to `both`. */
  axis?: StackedCardResizeAxis;
  /**
   * Pointer deltas are divided by this before they reach the policy, so a grip
   * inside a zoomed canvas tracks the cursor exactly. Keyboard nudges are
   * already in the host's units and pass straight through. Defaults to 1.
   */
  scale?: number;
  /** Accessible name for the grip. Defaults to "Resize card". */
  label?: string;
  /** Live feedback during the gesture. Never an undoable change. */
  onResize: (next: StackedCardSize, source: StackedCardResizeSource) => void;
  /** Fires once per gesture, and only if the size actually changed. */
  onResizeCommit: (
    final: StackedCardSize,
    source: StackedCardResizeSource
  ) => void;
}

/** The props to spread on the grip — `StackedCard`'s `resizeHandleProps`. */
export interface StackedCardResizeHandleProps {
  role: "separator";
  tabIndex: 0;
  "aria-label": string;
  "aria-controls": string;
  "aria-valuemin": number;
  "aria-valuemax": number;
  "aria-valuenow": number;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onLostPointerCapture: (e: React.PointerEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onKeyUp: (e: React.KeyboardEvent) => void;
}

const sameSize = (a: StackedCardSize, b: StackedCardSize): boolean =>
  a.w === b.w && a.h === b.h;

export const useStackedCardResize = ({
  size,
  policy,
  cardId,
  contentMin,
  axis = "both",
  scale = 1,
  label = "Resize card",
  onResize,
  onResizeCommit,
}: UseStackedCardResizeArgs): StackedCardResizeHandleProps => {
  // The pointer gesture's anchor: the size + content-min captured on pointer
  // down, plus the pointer origin the delta is measured from.
  const pointerStart = useRef<{
    start: StackedCardResizeStart;
    x: number;
    y: number;
  } | null>(null);
  // Mirrors the latest emitted size so pointer-up / key-up can commit exactly
  // what preview last showed, without a stale closure.
  const latest = useRef<StackedCardSize>(size);
  latest.current = size;
  // Whether this gesture actually moved the card. A bare click, or a Home on an
  // axis with no floor, leaves it false and commits nothing.
  const dirty = useRef(false);

  // Read through refs so the handlers below can stay stable across renders
  // while still seeing the current policy, axis and zoom.
  const policyRef = useRef(policy);
  policyRef.current = policy;
  const axisRef = useRef(axis);
  axisRef.current = axis;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const contentMinRef = useRef(contentMin);
  contentMinRef.current = contentMin;

  const readContentMin = useCallback((): StackedCardContentMin => {
    const source = contentMinRef.current;
    return typeof source === "function" ? source() : source;
  }, []);

  const emitPreview = useCallback(
    (next: StackedCardSize, source: StackedCardResizeSource) => {
      // Nothing changed — not a preview, and not something to commit later.
      if (sameSize(next, latest.current)) return;
      latest.current = next;
      dirty.current = true;
      onResize(next, source);
    },
    [onResize]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      pointerStart.current = {
        start: makeStart(latest.current, readContentMin()),
        x: e.clientX,
        y: e.clientY,
      };
      dirty.current = false;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      e.preventDefault();
    },
    [readContentMin]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const ps = pointerStart.current;
      if (!ps) return;
      const z = scaleRef.current || 1;
      const next = applyResize(
        ps.start,
        { dx: (e.clientX - ps.x) / z, dy: (e.clientY - ps.y) / z },
        policyRef.current,
        // Shift held during a free-form drag = temporary proportional lock.
        { constrain: e.shiftKey, axis: axisRef.current }
      );
      emitPreview(next, "pointer");
    },
    [emitPreview]
  );

  const endPointer = useCallback(() => {
    if (!pointerStart.current) return;
    pointerStart.current = null;
    if (dirty.current) onResizeCommit(latest.current, "pointer");
    dirty.current = false;
  }, [onResizeCommit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const next = keyboardResize(
        e.key,
        latest.current,
        policyRef.current,
        readContentMin(),
        { shiftKey: e.shiftKey, axis: axisRef.current }
      );
      if (!next) return;
      // The key is ours even when it resolves to no movement (already pinned at
      // a bound), so the browser must not also scroll the surface.
      e.preventDefault();
      emitPreview(next, "keyboard");
    },
    [emitPreview, readContentMin]
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isResizeKey(e.key)) return;
      // The gesture end for the keyboard is the key release: one commit per
      // hold, mirroring pointer-up after a stream of moves.
      if (dirty.current) onResizeCommit(latest.current, "keyboard");
      dirty.current = false;
    },
    [onResizeCommit]
  );

  return {
    role: "separator",
    tabIndex: 0,
    "aria-label": label,
    "aria-controls": cardId,
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    "aria-valuenow": measurePercent(size, policy, readContentMin()),
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: endPointer,
    onLostPointerCapture: endPointer,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
  };
};
